import type { ApplicationContext } from '@hc-frontend/core-context';
import type { JourneyAlias } from '@hc-frontend/core-types';
import {
  DomainSessionId,
  DomainUserId,
  HealthcareSessionId,
  HealthcareUserId,
  ProductType,
} from '@hc-frontend/core-types';
import type {
  IOperationResult,
  ISuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import { createErrorOperationResult } from '@hc-frontend/core-utils-business-operation';
import { z } from 'zod';

import { LeadStatus } from '../../entities';
import type {
  AuditLeadError,
  AuditRepository,
  GetProspectError,
  JourneyRepository,
  SaveActionError,
} from '../../repositories';
import type { GetProspectUseCase } from '../get-prospect/get-prospect';

export type ProspectToLeadUseCase = (
  healthcare: {
    userId: HealthcareUserId;
    sessionId: HealthcareSessionId;
  },
  domain: Partial<{
    userId: DomainUserId;
    sessionId: DomainSessionId;
  }>,
  product: ProductType,
  journeyAlias: JourneyAlias,
) => Promise<
  IOperationResult<void, GetProspectError | AuditLeadError | SaveActionError>
>;

export type ProspectToLeadFactoryParams = {
  journeyProvider: Pick<JourneyRepository, 'saveAction'>;
  auditProvider: Pick<AuditRepository, 'auditLead'>;
  getProspect: GetProspectUseCase;
  log: ApplicationContext['log'];
  auctionLead: (
    productType: ProductType,
    journeyAlias: JourneyAlias,
    domainSessionId: DomainSessionId,
  ) => Promise<ISuccessOperationResult<void>>;
};

export function prospectToLeadFactory({
  journeyProvider,
  auditProvider,
  getProspect,
  log,
  auctionLead,
}: ProspectToLeadFactoryParams): ProspectToLeadUseCase {
  return async (
    { userId, sessionId },
    { userId: domainUserId, sessionId: domainSessionId },
    product,
    journeyAlias,
  ) => {
    const parsedArguments = z
      .object({
        healthcare: z.object({
          userId: HealthcareUserId,
          sessionId: HealthcareSessionId,
        }),
        domain: z.object({
          userId: DomainUserId,
          sessionId: DomainSessionId,
        }),
        product: ProductType,
      })
      .safeParse({
        healthcare: {
          userId,
          sessionId,
        },
        domain: {
          userId: domainUserId,
          sessionId: domainSessionId,
        },
        product,
      });

    if (!parsedArguments.success) {
      log({
        type: 'error',
        context: 'server',
        message: 'failedToSaveAction',
        data: {
          rootCause: parsedArguments.error,
        },
      });
      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToSaveAction',
            rootCause: parsedArguments.error,
          },
        ],
      });
    }

    const result = await journeyProvider.saveAction(
      parsedArguments.data.healthcare.userId,
      parsedArguments.data.domain.sessionId,
      parsedArguments.data.product,
    );
    if (!result.success) {
      log({
        type: 'error',
        context: 'server',
        message: result.errors[0].code,
        data: {
          rootCause: result.errors[0].rootCause,
        },
      });
    }

    const prospectResult = await getProspect(
      sessionId,
      userId,
      domainSessionId,
      domainUserId,
      LeadStatus.Enum.SUBMITTED,
    );

    if (!prospectResult.success) {
      return prospectResult;
    }

    const auditResult = await auditProvider.auditLead(prospectResult.data);

    if (!auditResult.success) {
      log({
        type: 'error',
        context: 'server',
        message: auditResult.errors[0].code,
        data: {
          rootCause: auditResult.errors[0].rootCause,
        },
      });
    }

    return auctionLead(
      product,
      journeyAlias,
      domainSessionId as DomainSessionId,
    ); // At this point the domain session id is guaranteed to be defined
  };
}
